package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.CategoryRatingItem
import com.example.mystybag.appCore.model.pages.DashboardUIState
import com.example.mystybag.appCore.model.pages.DashboardPeriod
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_CAN_VIEW_REVENUE
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.ScrollToTopInterface
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.login.logic.NavigatorAddLocationPrompt
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantDashboardRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantReputationRequestModel
import com.example.mystybag.sharedmodels.project.logic.enums.ReservationStatus
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantReputationResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.ReservationItem
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime

class DashboardViewModel(context: AppContext): ChildViewModel<DashboardUIState>(context, DashboardUIState()),
    ScrollToTopInterface {

    /** Резервации след staff-филтър; периодът и обектът се филтрират локално без повторна заявка. */
    private var rawDashboardReservations: List<ReservationItem>? = null

    override fun onResume() {
        loadDashboardData()
    }

    fun loadDashboardData() {
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        if (merchantId == 0L) {
            return
        }

        scope.launch {
            // Refresh staff revenue-permission flag on every dashboard load so updates from the server propagate.
            val isStaffPrefs = Preferences.getBoolean(key = PREFERENCES_IS_STAFF, false)
            val canViewRevenuePrefs = !isStaffPrefs ||
                Preferences.getBoolean(key = PREFERENCES_STAFF_CAN_VIEW_REVENUE, false)
            updateState {
                it.copy(
                    isLoading = true,
                    isStaff = isStaffPrefs,
                    canViewRevenue = canViewRevenuePrefs
                )
            }

            commandBlock(showProgress = false, hideProgress = false) {
                CommonCommands.getMerchantDashboard(
                    GetMerchantDashboardRequestModel(merchantId = merchantId)
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantDashboardResponseModel
                                
                                // Get reservations from response (all reservations are transactions/payments)
                                var reservations = responseModel?.reservations ?: emptyList()
                                var locations = responseModel?.locations ?: emptyList()
                                // Staff: filter by allowed merchant_location_ids
                                val isStaff = Preferences.getBoolean(key = PREFERENCES_IS_STAFF, false)
                                val staffLocationIds = if (isStaff) {
                                    Preferences.getString(key = PREFERENCES_STAFF_LOCATION_IDS, "")
                                        .split(",").mapNotNull { it.trim().toLongOrNull() }.filter { it > 0L }
                                } else emptyList()
                                if (staffLocationIds.isNotEmpty()) {
                                    locations = locations.filter { it.id in staffLocationIds }
                                    reservations = reservations.filter { (it.locationId ?: 0L) in staffLocationIds }
                                }

                                rawDashboardReservations = reservations
                                val filtered = computeDashboardFilterResult(
                                    reservations,
                                    uiState.value.selectedLocationId,
                                    uiState.value.selectedPeriod,
                                    uiState.value.customPeriodFrom,
                                    uiState.value.customPeriodTo
                                )

                                updateState {
                                    it.copy(
                                        isLoading = false,
                                        bags = responseModel?.bags,
                                        locations = locations,
                                        companyPhoneNumber = responseModel?.companyPhoneNumber,
                                        companyDialCode = responseModel?.companyDialCode,
                                        registeredCompanyStreet = responseModel?.registeredCompanyStreet,
                                        registeredCompanyCityId = responseModel?.registeredCompanyCityId,
                                        registeredCompanyCityName = responseModel?.registeredCompanyCityName,
                                        soldBagsCount = filtered.soldBagsCount,
                                        soldBagUnitsTotal = filtered.soldBagUnitsTotal,
                                        totalRevenue = filtered.totalRevenue,
                                        toDeliverCount = filtered.toDeliverCount,
                                        unpaidAmount = filtered.unpaidAmount,
                                        paidAmount = filtered.paidAmount,
                                        reservations = filtered.reservations
                                    )
                                }

                                fetchMerchantReputation(merchantId)
                                
                                // If locations list is empty (merchant only), show prompt screen to add location
                                if (!isStaff && (responseModel?.locations ?: emptyList()).isEmpty()) {
                                    navigate(
                                        NavigatorAddLocationPrompt(
                                            merchantId = merchantId,
                                            companyPhoneNumber = responseModel?.companyPhoneNumber,
                                            companyDialCode = responseModel?.companyDialCode,
                                            registeredCompanyStreet = responseModel?.registeredCompanyStreet,
                                            registeredCompanyCityId = responseModel?.registeredCompanyCityId,
                                            registeredCompanyCityName = responseModel?.registeredCompanyCityName
                                        )
                                    )
                                }
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                // Handle error if needed
                            }
                        }
                    )
            }
        }
    }
    
    fun selectPeriod(period: DashboardPeriod) {
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        val today = LocalDate(now.year, now.monthNumber, now.dayOfMonth)
        updateState { state ->
            if (period == DashboardPeriod.PERIOD) {
                val from = state.customPeriodFrom ?: today
                val to = state.customPeriodTo ?: today
                val (f, t) = if (from <= to) Pair(from, to) else Pair(today, today)
                state.copy(selectedPeriod = period, customPeriodFrom = f, customPeriodTo = t)
            } else {
                state.copy(selectedPeriod = period)
            }
        }
        applyDashboardFiltersFromCache()
    }

    fun onCustomPeriodFromChanged(date: LocalDate?) {
        val currentTo = uiState.value.customPeriodTo
        val adjustedTo = if (currentTo != null && date != null && currentTo < date) date else currentTo
        updateState { it.copy(customPeriodFrom = date, customPeriodTo = adjustedTo) }
        applyDashboardFiltersFromCache()
    }

    fun onCustomPeriodToChanged(date: LocalDate?) {
        val currentFrom = uiState.value.customPeriodFrom
        val adjustedFrom = if (currentFrom != null && date != null && currentFrom > date) date else currentFrom
        updateState { it.copy(customPeriodFrom = adjustedFrom, customPeriodTo = date) }
        applyDashboardFiltersFromCache()
    }
    
    fun selectLocation(locationId: Long?) {
        updateState { it.copy(selectedLocationId = locationId) }
        applyDashboardFiltersFromCache()
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        if (merchantId != 0L) {
            fetchMerchantReputation(merchantId)
        }
    }

    /** Средна оценка и отзиви по избран обект (без пълен dashboard refresh). */
    private fun fetchMerchantReputation(merchantId: Long) {
        scope.launch {
            commandBlock(showProgress = false, hideProgress = false) {
                CommonCommands.getMerchantReputation(GetMerchantReputationRequestModel(merchantId = merchantId))
                    .onResponse(
                        success = { repResponse ->
                            launchOnMain {
                                applyReputationResponse(repResponse)
                            }
                        },
                        baseError = { }
                    )
            }
        }
    }

    private fun applyReputationResponse(repResponse: Any?) {
        val repModel = repResponse as? GetMerchantReputationResponseModel
        val allLocationRatings = repModel?.locationRatings ?: emptyList()
        val locationRatingsList = if (uiState.value.selectedLocationId == null) {
            allLocationRatings
        } else {
            allLocationRatings.filter { it.locationId == uiState.value.selectedLocationId }
        }
        val overallRating = if (locationRatingsList.isNotEmpty()) {
            locationRatingsList.mapNotNull { it.averageRating }.average()
        } else {
            0.0
        }
        val totalReviewCount = locationRatingsList.sumOf { it.reviewCount ?: 0 }
        val categoryKeysOrder = listOf(
            "review_rating_taste",
            "review_rating_quality",
            "review_rating_freshness",
            "review_rating_value"
        )
        val ratingsByKey = locationRatingsList
            .flatMap { it.categoryRatings }
            .groupBy { it.categoryKey }
        val categoryRatings = categoryKeysOrder.map { key ->
            val values = ratingsByKey[key].orEmpty().mapNotNull { it.averageRating }
            val avg = if (values.isNotEmpty()) values.average() else 0.0
            CategoryRatingItem(categoryKey = key, rating = avg)
        }
        updateState {
            it.copy(
                averageRating = overallRating,
                reviewCount = totalReviewCount,
                categoryRatings = categoryRatings
            )
        }
    }

    /**
     * Преизчислява KPI и списъка от кеша без мрежова заявка и без [DashboardUIState.isLoading].
     * При липса на кеш (първо отваряне) зарежда от сървъра.
     */
    private fun applyDashboardFiltersFromCache() {
        val raw = rawDashboardReservations ?: run {
            loadDashboardData()
            return
        }
        val s = uiState.value
        val filtered = computeDashboardFilterResult(
            raw,
            s.selectedLocationId,
            s.selectedPeriod,
            s.customPeriodFrom,
            s.customPeriodTo
        )
        updateState {
            it.copy(
                soldBagsCount = filtered.soldBagsCount,
                soldBagUnitsTotal = filtered.soldBagUnitsTotal,
                totalRevenue = filtered.totalRevenue,
                toDeliverCount = filtered.toDeliverCount,
                unpaidAmount = filtered.unpaidAmount,
                paidAmount = filtered.paidAmount,
                reservations = filtered.reservations
            )
        }
    }

    private data class DashboardFilterResult(
        val soldBagsCount: Int,
        val soldBagUnitsTotal: Int,
        val totalRevenue: Double,
        val toDeliverCount: Int,
        val unpaidAmount: Double,
        val paidAmount: Double,
        val reservations: List<ReservationItem>
    )

    private fun computeDashboardFilterResult(
        reservationsAfterStaff: List<ReservationItem>,
        selectedLocationId: Long?,
        period: DashboardPeriod,
        customFrom: LocalDate?,
        customTo: LocalDate?
    ): DashboardFilterResult {
        val byLocation = filterReservationsByLocation(reservationsAfterStaff, selectedLocationId)
        val filteredReservations = filterReservationsByPeriod(byLocation, period, customFrom, customTo)
        val reservationsForOverviewMetrics = filteredReservations.filter { isReservationIncludedInOverviewMetrics(it) }
        val filteredSoldBagsCount = reservationsForOverviewMetrics.size
        val filteredSoldBagUnitsTotal = reservationsForOverviewMetrics.sumOf { (it.bagsCount ?: 1).coerceAtLeast(1) }
        val filteredTotalRevenue = reservationsForOverviewMetrics.sumOf { it.amount ?: 0.0 }
        val (filteredUnpaidAmount, filteredPaidAmount) = calculateUnpaidAndExpected(filteredReservations)
        val toDeliverCount = reservationsForOverviewMetrics.count {
            ReservationStatus.fromInt(it.status ?: ReservationStatus.RESERVED.code) == ReservationStatus.PAID
        }
        val sortedVisibleReservations = reservationsForOverviewMetrics.sortedByDescending { it.id ?: 0L }
        return DashboardFilterResult(
            soldBagsCount = filteredSoldBagsCount,
            soldBagUnitsTotal = filteredSoldBagUnitsTotal,
            totalRevenue = filteredTotalRevenue,
            toDeliverCount = toDeliverCount,
            unpaidAmount = filteredUnpaidAmount,
            paidAmount = filteredPaidAmount,
            reservations = sortedVisibleReservations
        )
    }
    
    private fun filterReservationsByLocation(
        reservations: List<ReservationItem>,
        locationId: Long?
    ): List<ReservationItem> {
        return if (locationId == null) {
            reservations
        } else {
            reservations.filter { it.locationId == locationId }
        }
    }

    private fun filterReservationsByPeriod(
        reservations: List<ReservationItem>,
        period: com.example.mystybag.appCore.model.pages.DashboardPeriod,
        customFrom: LocalDate?,
        customTo: LocalDate?
    ): List<ReservationItem> {
        if (period == com.example.mystybag.appCore.model.pages.DashboardPeriod.PERIOD) {
            if (customFrom == null && customTo == null) return reservations
            return reservations.filter { r ->
                val d = parseReservationDate(r.dateTimeReserved) ?: return@filter false
                val fromOk = customFrom == null || d >= customFrom
                val toOk = customTo == null || d <= customTo
                fromOk && toOk
            }
        }
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        val today = LocalDate(now.year, now.monthNumber, now.dayOfMonth)
        val rangeStart = when (period) {
            com.example.mystybag.appCore.model.pages.DashboardPeriod.TODAY -> today
            com.example.mystybag.appCore.model.pages.DashboardPeriod.LAST_WEEK -> today.minus(DatePeriod(days = 7))
            com.example.mystybag.appCore.model.pages.DashboardPeriod.LAST_MONTH -> today.minus(DatePeriod(days = 30))
            else -> return reservations
        }
        return reservations.filter { r ->
            parseReservationDate(r.dateTimeReserved)?.let { d -> d in rangeStart..today } ?: false
        }
    }

    private fun isReservationIncludedInOverviewMetrics(reservation: ReservationItem): Boolean {
        val status = ReservationStatus.fromInt(reservation.status ?: ReservationStatus.RESERVED.code)
        return status == ReservationStatus.PAID ||
            status == ReservationStatus.DELIVERED_TO_CLIENT ||
            status == ReservationStatus.PAID_TO_MERCHANT
    }

    private fun parseReservationDate(dateTimeReserved: String?): LocalDate? {
        if (dateTimeReserved.isNullOrBlank()) return null
        return try {
            val ms = dateTimeReserved.trim().toLongOrNull()
            if (ms != null) {
                val instant = kotlinx.datetime.Instant.fromEpochMilliseconds(
                    if (ms < 1_000_000_000_000L) ms * 1000L else ms
                )
                val ld = instant.toLocalDateTime(TimeZone.currentSystemDefault())
                LocalDate(ld.year, ld.monthNumber, ld.dayOfMonth)
            } else {
                LocalDate.parse(dateTimeReserved.take(10))
            }
        } catch (_: Exception) {
            null
        }
    }
    
    private fun calculateUnpaidAndExpected(
        reservations: List<com.example.mystybag.sharedmodels.project.responseModels.merchant.ReservationItem>
    ): Pair<Double, Double> {
        // Calculate unpaid and paid amounts based on settlement_date
        // If settlement_date is null - money is waiting for settlement (unpaid/expected)
        // If settlement_date exists - money has been paid to external account (paid)
        var unpaid = 0.0
        var paid = 0.0
        
        reservations.forEach { reservation ->
            val amount = reservation.amount ?: 0.0
            val settlementDate = reservation.settlementDate
            if (settlementDate != null && settlementDate.isNotEmpty()) {
                // Money has been paid to external account
                paid += amount
            } else {
                // Money is waiting for settlement date - this is unpaid/expected
                unpaid += amount
            }
        }
        
        return Pair(unpaid, paid) // unpaid = expected/unpaid, paid = already settled
    }

    override fun scrollToTop() {
        // Implement scroll to top if needed
    }
}

